import type { FilterableCarItem } from '@/scopes/checkout/filterableCars/domain/types'
import { Button } from '@sunwebgroup/uds-ui/button'
import { sub } from 'date-fns'
import * as React from 'react'
import { useIntl } from '@/intl'
import {
  ActionButtons,
  CarCardBody,
  CarCardFooter,
  CarCardFooterSection,
  CarItem,
  CarRentalCard,
  Price,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/CarOption'
import styles from '@/scopes/checkout/components/services/CarRentalFilteringService/CarOption.module.css'
import {
  carIsBeingUpgraded,
  carSelectionLoading,
  carToBeUpgraded,
  editingCar,
  useCarRentalFilteringServiceStore,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/store'
import { addCarToOptionAndUpgradeCar } from '@/scopes/checkout/domain/actions/services/carRentalFiltering'
import { createCarEvent } from '@/scopes/checkout/domain/analytics'
import { useBookingDraftQuery, useService } from '@/scopes/checkout/domain/hooks'
import { ServiceType } from '@/scopes/checkout/domain/types'
import { useGetAllCarsQuery, useSelectCar } from '@/scopes/checkout/filterableCars/domain/hooks'
import {
  buildUniqOptionCarId,
} from '@/scopes/checkout/infrastructure/api/service-mappers/carRentalFilteringServiceMapper'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'

export function CarView({ ...car }: FilterableCarItem) {
  return (
    <CarItem key={car.id} isSelected={false}>
      <CarCardView {...car} />
    </CarItem>
  )
}

export function CarCardView({ ...option }: FilterableCarItem) {
  const { value, id, price, isIncluded } = option
  const { sendEvent } = useAnalytics()
  const { service, setServiceValue } = useService(ServiceType.CarRentalFiltering)
  const { selectCar } = useSelectCar()
  const { isFetching: isGettingBookingDraft } = useBookingDraftQuery()
  const { startEditingCar, setCarToBeSelected, setShowFilterableCars, setShowExtraCars, setCarIsBeingUpgraded, setCarToBeUpgraded, endEditingCar } = useCarRentalFilteringServiceStore()
  const isCarBeingUpgraded = useCarRentalFilteringServiceStore(carIsBeingUpgraded)
  const isCarBeingSelected = useCarRentalFilteringServiceStore(carSelectionLoading)
  const carValueToBeUpgraded = useCarRentalFilteringServiceStore(carToBeUpgraded)
  const { data: allCarsData } = useGetAllCarsQuery()
  const { configurations } = service || {}
  const defaultDriverBirthdate = sub(new Date(), { years: configurations?.rentalConditionsMinAgeDriver })

  const currentEditingCar = useCarRentalFilteringServiceStore(editingCar(carValueToBeUpgraded))
  const intl = useIntl()

  const handleSelectClick = React.useCallback(async () => {
    if (!id)
      return
    if (!service)
      return
    const cars = allCarsData?.cars ?? {}
    const carIdUnique = buildUniqOptionCarId(id, value)

    if (isCarBeingUpgraded) {
      // Upgrading an existing car we need this hook to be called before setting the new service value
      await selectCar(id)
      setCarIsBeingUpgraded(false)
      if (currentEditingCar) {
        setServiceValue(addCarToOptionAndUpgradeCar({ value, participantId: currentEditingCar.driverId ?? '', id })(service, cars))
      }
      setCarToBeUpgraded('')
      endEditingCar(carIdUnique)
      sendEvent(
        createCarEvent({
          result: 'upgrade',
          type: value,
          description: 'upgrade button clicked',
          element: 'button',
        }),
      )
    }
    else {
      setCarToBeSelected(carIdUnique)
      setShowExtraCars(false)
      setShowFilterableCars(false)
      // Selecting a new car we need this hook to be called after closing the filterable cars list
      await selectCar(id)
      startEditingCar(carIdUnique, {
        driverId: null,
        driverBirthDate: new Date(defaultDriverBirthdate),
        driverHasCreditCard: false,
        bookedEquipmentsWithQuantity: [],
        isEditing: false,
      })
      sendEvent(
        createCarEvent({
          result: 'Car selected',
          type: value,
          description: 'Filtered car selected',
          element: 'button',
          validated: true,
        }),
      )
    }
  }, [defaultDriverBirthdate, allCarsData, currentEditingCar, endEditingCar, isCarBeingUpgraded, service, setCarIsBeingUpgraded, setCarToBeUpgraded, setServiceValue, selectCar, setShowFilterableCars, sendEvent, setCarToBeSelected, setShowExtraCars, startEditingCar, value, id])

  if (!service)
    return
  return (
    <CarRentalCard>
      <CarCardBody {...option} />
      <CarCardFooterSection>
        <CarCardFooter>
          <Price>
            {intl.formatTruncatedCurrency(price, { showSign: isIncluded })}
          </Price>
          <ActionButtons>
            <Button variant="secondary" onClick={handleSelectClick} size="S" disabled={isCarBeingSelected || isGettingBookingDraft} className={styles.actionButton}>
              {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_select_car_text' })}
            </Button>
          </ActionButtons>
        </CarCardFooter>
      </CarCardFooterSection>
    </CarRentalCard>
  )
}
